import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

/**
 * Hook: provides sorted/filtered data + column sort state with optional
 * localStorage persistence. Returns helpers for header rendering and bulk select.
 *
 * Usage:
 *   const { sorted, sortKey, sortDir, onSort, sortHeader, selected, toggle, clearSelected, allSelected, toggleAll } =
 *       useSortableList(items, { storageKey: "invoices", initial: { key: "date", dir: "desc" }, search, searchKeys: ["number", "customer_name"], idKey: "id" });
 */
export function useSortableList(items, opts = {}) {
    const {
        storageKey,
        initial = { key: null, dir: "asc" },
        search = "",
        searchKeys = [],
        idKey = "id",
        numericDescDefault = ["amount", "total", "balance", "balance_due", "outstanding", "date", "due_date"],
    } = opts;

    const stored = (() => {
        if (!storageKey) return null;
        try {
            const v = JSON.parse(localStorage.getItem(`vyaahar_sort_${storageKey}`) || "null");
            return v && v.key ? v : null;
        } catch { return null; }
    })();

    const [sortKey, setSortKey] = useState(stored?.key ?? initial.key);
    const [sortDir, setSortDir] = useState(stored?.dir ?? initial.dir);
    const [selected, setSelected] = useState({});

    useEffect(() => {
        if (!storageKey) return;
        try { localStorage.setItem(`vyaahar_sort_${storageKey}`, JSON.stringify({ key: sortKey, dir: sortDir })); } catch { /* ignore */ }
    }, [storageKey, sortKey, sortDir]);

    const onSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(numericDescDefault.includes(key) ? "desc" : "asc");
        }
    };

    const sorted = useMemo(() => {
        const lc = (search || "").toLowerCase();
        let arr = !lc ? [...items] : items.filter((it) =>
            searchKeys.some((k) => String(it[k] ?? "").toLowerCase().includes(lc))
        );
        if (sortKey) {
            const dir = sortDir === "asc" ? 1 : -1;
            arr.sort((a, b) => {
                const av = a[sortKey] ?? "";
                const bv = b[sortKey] ?? "";
                if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
                if (av instanceof Date || bv instanceof Date) return (new Date(av) - new Date(bv)) * dir;
                return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * dir;
            });
        }
        return arr;
    }, [items, search, searchKeys, sortKey, sortDir]);

    const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const clearSelected = () => setSelected({});
    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
    const allSelected = sorted.length > 0 && sorted.every((r) => selected[r[idKey]]);
    const toggleAll = () => {
        if (allSelected) clearSelected();
        else setSelected(Object.fromEntries(sorted.map((r) => [r[idKey], true])));
    };

    return { sorted, sortKey, sortDir, onSort, selected, selectedIds, toggle, clearSelected, allSelected, toggleAll };
}

/**
 * Render a single sortable column header. Pass `align="right"` for numeric columns.
 */
export function SortHeader({ label, sortKey, currentKey, dir, onSort, align = "left", testId }) {
    const active = sortKey === currentKey;
    const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
    const justify = align === "right" ? "flex-row-reverse" : "";
    return (
        <th className={align === "right" ? "num" : ""}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                data-testid={testId || `sort-${sortKey}`}
                className={`inline-flex items-center gap-1 select-none cursor-pointer transition-colors ${justify} ${active ? "text-foreground" : "hover:text-foreground"}`}
            >
                <span>{label}</span>
                <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} strokeWidth={2.2} />
            </button>
        </th>
    );
}
